import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, uploadFile } from '../shared/api';
import { Page, Crumbs, Card, Btn, TextInput, Pill, DataTable, ui, type Col } from '../shared/ui';

type Locale = 'hr' | 'en';
type ZapisnikListRow = {
    id: number;
    public_id: string;
    title: string;
    meeting_date: string;
    file_name: string;
    file_mime: string | null;
    file_size_bytes: number | null;
    ai_status: 'pending' | 'parsed' | 'parse_failed' | 'skipped';
    status: string;
    published_at: string | null;
    created_at: string;
    uploaded_by_name: string | null;
    signers_total: number;
    signers_done: number;
    actions_total: number;
    actions_done: number;
};
type ListResp = { zapisnici: ZapisnikListRow[]; can_upload: boolean; is_signer: boolean };

type Signer = { id: number; user_id: number | null; signer_name: string; signer_email: string | null; signed_at: string | null; sign_token: string };
type Action = {
    id: number; task_id: number | null; action_summary: string;
    assignee_hint: string | null; assignee_user_id: number | null;
    due_date: string | null; priority: string; context_excerpt: string | null;
    task_title: string | null; task_completed_at: string | null; task_status: string | null;
    assignee_name: string | null;
};
type ShowResp = {
    zapisnik: {
        id: number; public_id: string; title: string; meeting_date: string;
        file_name: string; file_mime: string | null; file_size_bytes: number | null;
        ai_status: string; ai_summary: string | null; ai_error: string | null;
        status: string; published_at: string | null; uploaded_by_name: string | null; created_at: string;
    };
    signers: Signer[];
    actions: Action[];
    my_sign: { id: number; signed_at: string | null } | null;
    can_upload: boolean;
};

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const fmtDate = (s: string | null) => {
    if (!s) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}.` : s;
};
const fmtDateTime = (s: string | null) => {
    if (!s) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}. ${m[4]}:${m[5]}` : s;
};
const fmtSize = (b: number | null) => {
    if (!b) return '';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
    return (b / 1024 / 1024).toFixed(1) + ' MB';
};

export function ZapisniciModule({ token, locale, onBack }: { token: string | null; locale: Locale; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ListResp | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState<number | null>(null);
    const [showUpload, setShowUpload] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true); setError('');
        try {
            const q = search.trim();
            const r = await api<ListResp>('/zapisnici' + (q ? '?q=' + encodeURIComponent(q) : ''), token);
            setData(r);
        } catch (e) { setError(String(e)); }
        setLoading(false);
    }, [token, search]);

    useEffect(() => { void load(); }, [load]);

    // Auto-osvježavanje dok ima pending zapisnika
    useEffect(() => {
        const hasPending = data?.zapisnici.some((z) => z.ai_status === 'pending');
        if (!hasPending) return;
        const interval = setInterval(() => { void load(); }, 4000);
        return () => clearInterval(interval);
    }, [data?.zapisnici, load]);

    const filtered = data?.zapisnici ?? [];

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    if (isHrms) {
        const cols: Col<ZapisnikListRow>[] = [
            {
                key: 'title', label: t('Zapisnik', 'Minutes'), render: (z) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <strong style={{ color: ui.ink }}>📑 {z.title}</strong>
                        <span style={{ fontSize: '0.74rem', color: ui.muted }}>{z.file_name} · {fmtSize(z.file_size_bytes)}</span>
                    </div>
                ),
            },
            { key: 'meeting_date', label: t('Datum sjednice', 'Meeting date'), render: (z) => <Pill tone="info">📅 {fmtDate(z.meeting_date)}</Pill> },
            {
                key: 'signers', label: t('Potpisi', 'Signatures'), align: 'center',
                render: (z) => {
                    const done = z.signers_total > 0 && z.signers_done >= z.signers_total;
                    return <Pill tone={done ? 'ok' : 'neutral'}>✍ {z.signers_done}/{z.signers_total}</Pill>;
                },
            },
            { key: 'actions', label: t('Action points', 'Action points'), align: 'center', render: (z) => <span style={{ color: ui.ink2 }}>📋 {z.actions_done}/{z.actions_total}</span> },
            {
                key: 'ai', label: 'AI', align: 'center', render: (z) =>
                    z.ai_status === 'parsed' ? <Pill tone="ok">🤖 ✓</Pill>
                    : z.ai_status === 'parse_failed' ? <Pill tone="bad">🤖 ⚠</Pill>
                    : z.ai_status === 'pending' ? <Pill tone="warn">🤖 …</Pill>
                    : <span style={{ color: ui.muted }}>🤖 —</span>,
            },
            {
                key: 'created', label: t('Uploadano', 'Uploaded'), align: 'right', render: (z) => (
                    <span style={{ fontSize: '0.78rem', color: ui.muted }}>
                        {z.uploaded_by_name ? z.uploaded_by_name + ' · ' : ''}{fmtDateTime(z.created_at)}
                    </span>
                ),
            },
        ];
        return (
            <Page>
                {onBack ? <div><Btn variant="ghost" size="sm" onClick={onBack}>← {t('Natrag', 'Back')}</Btn></div> : null}
                <Crumbs items={[t('Dashboard', 'Dashboard'), t('Zapisnici uprave', 'Board Minutes')]} />
                <Card pad={14}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextInput value={search} onChange={setSearch} icon="🔍" placeholder={t('Pretraži naslov / sažetak…', 'Search title / summary…')} width={320} />
                        <div style={{ flex: 1 }} />
                        {data?.can_upload ? <Btn variant="primary" onClick={() => setShowUpload(true)}>＋ {t('Novi zapisnik', 'New minutes')}</Btn> : null}
                    </div>
                </Card>
                {error ? <div style={{ padding: 10, background: '#fde7ea', color: '#c0264a', borderRadius: ui.radiusSm, fontSize: '0.84rem' }}>{error}</div> : null}
                <Card pad={0}>
                    <DataTable cols={cols} rows={loading ? null : filtered} onRowClick={(z) => setOpenId(z.id)}
                        empty={search.trim() ? t('Nema rezultata za pretragu.', 'No search results.') : t('Još nema zapisnika. Iva može uploadati prvi (gumb gore desno).', 'No minutes yet. Iva can upload the first one (button top right).')} />
                </Card>
                {openId !== null ? <ZapisnikDrawer id={openId} token={token} locale={locale} onClose={() => setOpenId(null)} onChanged={() => { void load(); }} /> : null}
                {showUpload ? <UploadModal token={token} locale={locale} onClose={() => setShowUpload(false)} onUploaded={(id) => { setShowUpload(false); void load(); setOpenId(id); }} /> : null}
            </Page>
        );
    }

    return (
        <section className="card admin-block">
            {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ marginBottom: 8 }}>← {t('Natrag', 'Back')}</button> : null}
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📑</div>
                <div className="bi-module-hero-text">
                    <h1>12 · {t('Zapisnici uprave', 'Board Minutes')}</h1>
                    <p className="muted">{t('Zapisnici sjednica uprave — upload, potpisi članova, AI ekstrakcija action pointsa s automatskim kreiranjem zadataka u m.11.1 Uprava timu.', 'Board meeting minutes — upload, member signatures, AI action point extraction with auto-creation of m.11.1 Uprava team tasks.')}</p>
                </div>
            </div>

            {/* Search + upload toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('🔍 Pretraži naslov / sažetak…', '🔍 Search title / summary…')}
                    style={{ flex: '1 1 280px', maxWidth: 480, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--ad-acc)', fontSize: '0.92rem' }} />
                {data?.can_upload ? (
                    <button type="button" onClick={() => setShowUpload(true)}
                        style={{ padding: '8px 18px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem' }}>
                        + {t('Novi zapisnik', 'New minutes')}
                    </button>
                ) : null}
            </div>

            {error ? <div style={{ padding: 10, background: '#fde8e8', color: '#b14040', borderRadius: 6, marginBottom: 10 }}>{error}</div> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {!loading && filtered.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#888', border: '1px dashed #ccc', borderRadius: 8 }}>
                    {search.trim() ? t('Nema rezultata za pretragu.', 'No search results.') : t('Još nema zapisnika. Iva može uploadati prvi (gumb gore desno).', 'No minutes yet. Iva can upload the first one (button top right).')}
                </div>
            ) : null}

            {/* List */}
            <div style={{ display: 'grid', gap: 8 }}>
                {filtered.map((z) => {
                    const signPct = z.signers_total > 0 ? Math.round((z.signers_done / z.signers_total) * 100) : 0;
                    const actionPct = z.actions_total > 0 ? Math.round((z.actions_done / z.actions_total) * 100) : 0;
                    return (
                        <button key={z.id} type="button" onClick={() => setOpenId(z.id)}
                            style={{
                                textAlign: 'left', width: '100%', padding: 14,
                                background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)',
                                borderRadius: 10, cursor: 'pointer',
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <strong style={{ color: BROWN, fontSize: '1rem' }}>📑 {z.title}</strong>
                                <span style={{ background: GOLD, color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '0.74rem', fontWeight: 700 }}>
                                    📅 {fmtDate(z.meeting_date)}
                                </span>
                                <span style={{ marginLeft: 'auto', fontSize: '0.74rem', color: '#888' }}>
                                    {z.uploaded_by_name ? t('Uploadao', 'By') + ' ' + z.uploaded_by_name + ' · ' : ''}
                                    {fmtDateTime(z.created_at)}
                                </span>
                            </div>
                            <div style={{ marginTop: 6, display: 'flex', gap: 14, fontSize: '0.82rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                <span title={t('Potpisali', 'Signed')}>
                                    ✍ <strong style={{ color: signPct >= 100 ? '#1f7a36' : BROWN }}>{z.signers_done}/{z.signers_total}</strong>
                                </span>
                                <span title={t('Action points', 'Action points')}>
                                    📋 <strong style={{ color: BROWN }}>{z.actions_done}/{z.actions_total}</strong>
                                </span>
                                <span title={t('AI status', 'AI status')}>
                                    {z.ai_status === 'parsed' ? <span style={{ color: '#1f7a36' }}>🤖 AI ✓</span>
                                    : z.ai_status === 'parse_failed' ? <span style={{ color: '#b14040' }}>🤖 AI ⚠</span>
                                    : z.ai_status === 'pending' ? <span style={{ color: '#9a6b12' }}>🤖 AI…</span>
                                    : <span style={{ color: '#888' }}>🤖 —</span>}
                                </span>
                                <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.74rem' }}>
                                    {z.file_name} · {fmtSize(z.file_size_bytes)}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {openId !== null ? <ZapisnikDrawer id={openId} token={token} locale={locale} onClose={() => setOpenId(null)} onChanged={() => { void load(); }} /> : null}
            {showUpload ? <UploadModal token={token} locale={locale} onClose={() => setShowUpload(false)} onUploaded={(id) => { setShowUpload(false); void load(); setOpenId(id); }} /> : null}
        </section>
    );
}

function ZapisnikDrawer({ id, token, locale, onClose, onChanged }: { id: number; token: string | null; locale: Locale; onClose: () => void; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ShowResp | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        try {
            const r = await api<ShowResp>(`/zapisnici/${id}`, token);
            setData(r);
        } catch { /* */ }
        setBusy(false);
    }, [token, id]);

    useEffect(() => { void load(); }, [load]);

    // Auto-osvježavanje dok je status pending
    useEffect(() => {
        if (data?.zapisnik.ai_status !== 'pending') return;
        const interval = setInterval(() => { void load(); }, 4000);
        return () => clearInterval(interval);
    }, [data?.zapisnik.ai_status, load]);

    const sign = async () => {
        if (!token) return;
        try {
            await mutate(`/zapisnici/${id}/sign`, token, 'POST');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    const reparse = async () => {
        if (!token) return;
        if (!confirm(t('Ponovno parsiraj AI? Postojeći action points i zadaci se brišu.', 'Reparse AI? Existing action points and tasks will be deleted.'))) return;
        try {
            await mutate(`/zapisnici/${id}/reparse`, token, 'POST');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    const destroyZap = async () => {
        if (!token) return;
        if (!confirm(t('Obriši zapisnik?', 'Delete minutes?'))) return;
        try {
            await mutate(`/zapisnici/${id}`, token, 'DELETE');
            onClose(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 80, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(820px, 96vw)', background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-6px 0 20px rgba(0,0,0,0.15)' }}>
                <div style={{ position: 'sticky', top: 0, background: 'var(--ad-bg)', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.3)', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>
                    <strong style={{ color: BROWN, fontSize: '1.05rem' }}>📑 {data?.zapisnik.title ?? '...'}</strong>
                    {data ? <span style={{ background: GOLD, color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 800 }}>📅 {fmtDate(data.zapisnik.meeting_date)}</span> : null}
                    <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid #ccc', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}>✕</button>
                </div>

                {busy && !data ? <p className="muted" style={{ padding: 18 }}>{t('Učitavam…', 'Loading…')}</p> : null}

                {data ? (
                    <div style={{ padding: 18 }}>
                        {/* File + actions */}
                        <div style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <a href={`/api/zapisnici/${id}/file?_token=${token}`} target="_blank" rel="noreferrer"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (!token) return;
                                    const resp = await fetch(`/api/zapisnici/${id}/file`, { headers: { Authorization: `Bearer ${token}` } });
                                    const blob = await resp.blob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                }}
                                style={{ padding: '7px 14px', background: BROWN, color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.84rem', fontWeight: 700 }}>
                                📂 {t('Otvori datoteku', 'Open file')}
                            </a>
                            {data.can_upload ? (
                                <>
                                    <button type="button" onClick={() => void reparse()} style={{ padding: '7px 14px', background: '#1e40af', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>
                                        🤖 {t('Ponovo AI parse', 'Reparse AI')}
                                    </button>
                                    <button type="button" onClick={() => void destroyZap()} style={{ padding: '7px 14px', background: 'transparent', color: '#b14040', border: '1px solid #b14040', borderRadius: 6, cursor: 'pointer', fontSize: '0.84rem' }}>
                                        🗑 {t('Obriši', 'Delete')}
                                    </button>
                                </>
                            ) : null}
                        </div>

                        {/* AI summary */}
                        {data.zapisnik.ai_summary ? (
                            <div style={{ marginBottom: 14, padding: 12, background: 'var(--ad-bg)', borderRadius: 6, fontSize: '0.86rem', color: '#444', whiteSpace: 'pre-wrap', borderLeft: `3px solid ${GOLD}` }}>
                                <div style={{ fontWeight: 700, color: BROWN, marginBottom: 6 }}>🤖 {t('AI sažetak', 'AI summary')}</div>
                                {data.zapisnik.ai_summary}
                            </div>
                        ) : null}
                        {data.zapisnik.ai_status === 'parse_failed' && data.zapisnik.ai_error ? (
                            <div style={{ marginBottom: 14, padding: 10, background: '#fde8e8', borderRadius: 6, color: '#b14040', fontSize: '0.82rem' }}>
                                ⚠ {t('AI parse fail', 'AI parse fail')}: {data.zapisnik.ai_error}
                            </div>
                        ) : null}

                        {/* Signers */}
                        <div style={{ marginBottom: 14 }}>
                            <div style={{ fontWeight: 700, color: BROWN, fontSize: '0.92rem', marginBottom: 6 }}>✍ {t('Potpisi članova uprave', 'Board signatures')} ({data.signers.filter((s) => s.signed_at).length}/{data.signers.length})</div>
                            <div style={{ display: 'grid', gap: 6 }}>
                                {data.signers.map((s) => (
                                    <div key={s.id} style={{ padding: '8px 10px', background: s.signed_at ? 'rgba(31,122,54,0.06)' : '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontWeight: 700, color: s.signed_at ? '#1f7a36' : BROWN }}>{s.signed_at ? '✓' : '○'}</span>
                                        <span style={{ flex: 1 }}>{s.signer_name}{s.user_id === null ? ' (external)' : ''}</span>
                                        <span style={{ fontSize: '0.74rem', color: '#888' }}>{s.signed_at ? fmtDateTime(s.signed_at) : t('nije potpisao', 'not signed')}</span>
                                    </div>
                                ))}
                            </div>
                            {data.my_sign && !data.my_sign.signed_at ? (
                                <button type="button" onClick={() => void sign()}
                                    style={{ marginTop: 8, padding: '8px 18px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.86rem' }}>
                                    ✍ {t('Potpiši ovaj zapisnik', 'Sign this minutes')}
                                </button>
                            ) : null}
                            {data.my_sign?.signed_at ? (
                                <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#1f7a36' }}>✓ {t('Potpisao/la si', 'You signed')} {fmtDateTime(data.my_sign.signed_at)}</div>
                            ) : null}
                        </div>

                        {/* Action points */}
                        <div>
                            <div style={{ fontWeight: 700, color: BROWN, fontSize: '0.92rem', marginBottom: 6 }}>📋 {t('Action points', 'Action points')} ({data.actions.length})</div>
                            {data.actions.length === 0 ? <p className="muted" style={{ fontSize: '0.82rem' }}>{t('AI nije izvukao action points ili još nije parsiran.', 'AI did not extract action points or has not parsed yet.')}</p> : null}
                            {data.actions.map((a) => (
                                <div key={a.id} style={{ marginBottom: 8, padding: 10, background: '#fff', border: `1px solid rgba(var(--ad-soft-rgb),0.3)`, borderLeft: `4px solid ${a.task_completed_at ? '#1f7a36' : a.priority === 'urgent' ? '#b14040' : GOLD}`, borderRadius: 5 }}>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                        <strong style={{ color: BROWN, fontSize: '0.9rem', textDecoration: a.task_completed_at ? 'line-through' : 'none' }}>{a.action_summary}</strong>
                                        {a.priority === 'urgent' ? <span style={{ background: '#b14040', color: '#fff', padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700 }}>HITNO</span> : null}
                                        {a.task_completed_at ? <span style={{ background: '#1f7a36', color: '#fff', padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700 }}>✓ ZAVRŠEN</span> : null}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#666', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                        <span>👤 <strong>{a.assignee_name ?? a.assignee_hint ?? t('— nije dodjeljen —', '— unassigned —')}</strong></span>
                                        {a.due_date ? <span>📅 {fmtDate(a.due_date)}</span> : null}
                                        {a.task_id ? <span>🔗 task #{a.task_id}</span> : null}
                                    </div>
                                    {a.context_excerpt ? (
                                        <div style={{ marginTop: 6, padding: 6, background: '#fafafa', borderRadius: 4, fontSize: '0.76rem', color: '#666', fontStyle: 'italic' }}>
                                            „{a.context_excerpt}"
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function UploadModal({ token, locale, onClose, onUploaded }: { token: string | null; locale: Locale; onClose: () => void; onUploaded: (id: number) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [title, setTitle] = useState('');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState('');

    const send = async () => {
        if (!token) return;
        if (!title.trim()) { alert(t('Naslov je obavezan', 'Title required')); return; }
        if (!file) { alert(t('Odaberi datoteku', 'Pick a file')); return; }
        setBusy(true); setProgress(t('Šaljem datoteku + AI parsira…', 'Uploading + AI parsing…'));
        try {
            const fd = new FormData();
            fd.append('title', title.trim());
            fd.append('meeting_date', date);
            fd.append('file', file);
            const r = await uploadFile<{ ok: boolean; id: number; ai: { action_count: number; status: string } }>('/zapisnici', token, fd);
            if (r.ok) {
                setProgress(t('Gotovo! AI status: ', 'Done! AI: ') + r.ai.status + ' · ' + r.ai.action_count + ' action point' + (r.ai.action_count === 1 ? '' : 's'));
                setTimeout(() => onUploaded(r.id), 1200);
            }
        } catch (e) { alert(String(e)); }
        setBusy(false);
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 22, width: 'min(540px, 92vw)' }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>📑 {t('Novi zapisnik uprave', 'New board minutes')}</h3>
                <label style={{ display: 'block', marginTop: 10 }}>
                    <span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Naslov', 'Title')}</span>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
                        placeholder={t('npr. „Sjednica uprave 09.06.2026."', 'e.g. "Board meeting 09.06.2026"')}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.92rem' }} />
                </label>
                <label style={{ display: 'block', marginTop: 12 }}>
                    <span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Datum sjednice', 'Meeting date')}</span>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.92rem' }} />
                </label>
                <label style={{ display: 'block', marginTop: 12 }}>
                    <span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Datoteka (PDF ili DOCX, max 25 MB)', 'File (PDF or DOCX, max 25 MB)')}</span>
                    <input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.86rem' }} />
                    {file ? <div style={{ marginTop: 4, fontSize: '0.76rem', color: '#888' }}>📂 {file.name} · {fmtSize(file.size)}</div> : null}
                </label>
                {progress ? <div style={{ marginTop: 10, padding: 8, background: 'rgba(31,122,54,0.08)', borderRadius: 6, fontSize: '0.82rem', color: '#1f7a36' }}>{progress}</div> : null}
                <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void send()} style={{ padding: '7px 18px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? '⏳' : '🤖 ' + t('Upload + AI parse', 'Upload + AI parse')}</button>
                </div>
            </div>
        </div>
    );
}
